import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, User, Building, Phone, RefreshCw, FileText, MapPin } from 'lucide-react';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';

interface CadastroData {
  nome: string;
  email: string;
  senha: string;
  confirmarSenha: string;
  empresa: string;
  telefone: string;
  nome_empresa?: string;
  cnpj?: string;
  endereco?: string;
  responsavel?: string;
}

const Cadastro: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tipoUsuario, setTipoUsuario] = useState<'cliente' | 'fornecedor'>('cliente');

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<CadastroData>();
  const senha = watch('senha');

  const generateRandomPassword = (length: number): string => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let password = '';
    const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%&*';
    password += upperCase[Math.floor(Math.random() * upperCase.length)];
    password += lowerCase[Math.floor(Math.random() * lowerCase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const generateSecurePassword = () => {
    const securePassword = generateRandomPassword(8);
    setValue('senha', securePassword);
    setValue('confirmarSenha', securePassword);
    toast.success('Senha segura gerada e aplicada!');
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    score = Object.values(checks).filter(Boolean).length;
    if (score <= 2) return { score, label: 'Fraca', color: 'text-red-600' };
    if (score === 3) return { score, label: 'Média', color: 'text-yellow-600' };
    if (score === 4) return { score, label: 'Forte', color: 'text-blue-600' };
    return { score, label: 'Muito Forte', color: 'text-green-600' };
  };

  const onSubmit = async (data: CadastroData) => {
    if (data.senha !== data.confirmarSenha) {
      toast.error('As senhas não coincidem');
      return;
    }
    setIsLoading(true);
    try {
      if (tipoUsuario === 'cliente') {
        await axios.post('/api/clientes/cadastro', {
          nome: data.nome,
          email: data.email,
          senha: data.senha,
          empresa: data.empresa,
          telefone: data.telefone
        });
      } else {
        await axios.post('/api/fornecedores/cadastro', {
          nome_empresa: data.nome_empresa,
          cnpj: data.cnpj,
          responsavel: data.nome,
          email: data.email,
          telefone: data.telefone,
          endereco: data.endereco,
          senha: data.senha
        });
      }
      toast.success('Cadastro realizado com sucesso!');
      navigate('/login');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao realizar cadastro');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={tipoUsuario === 'cliente' ? 'Seja um Cliente' : 'Seja um Fornecedor'}
      subtitle={tipoUsuario === 'cliente' ? 'Encontre as peças que precisa' : 'Cadastre sua empresa e comece a receber pedidos'}
    >
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <label className="form-label text-center block">Tipo de Cadastro</label>
          <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mt-3">
            <button
              type="button"
              onClick={() => setTipoUsuario('cliente')}
              className={`p-4 rounded-lg border-2 transition-all ${tipoUsuario === 'cliente' ? 'border-red bg-red/5 text-red' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
            >
              <div className="flex flex-col items-center space-y-2">
                <User className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-medium">Cliente</p>
                  <p className="text-xs text-gray-500">Buscar peças</p>
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setTipoUsuario('fornecedor')}
              className={`p-4 rounded-lg border-2 transition-all ${tipoUsuario === 'fornecedor' ? 'border-red bg-red/5 text-red' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
            >
              <div className="flex flex-col items-center space-y-2">
                <Building className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-medium">Fornecedor</p>
                  <p className="text-xs text-gray-500">Vender peças</p>
                </div>
              </div>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="card w-full">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="form-label">Nome Completo</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    {...register('nome', { required: 'Nome é obrigatório' })}
                    className="form-input pl-10 w-full"
                    placeholder="Seu nome completo"
                  />
                </div>
                {errors.nome && <p className="text-red text-sm mt-1">{errors.nome.message}</p>}
              </div>
              <div>
                <label className="form-label">E-mail</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    {...register('email', { required: 'E-mail é obrigatório' })}
                    className="form-input pl-10 w-full"
                    placeholder="seu@email.com"
                  />
                </div>
                {errors.email && <p className="text-red text-sm mt-1">{errors.email.message}</p>}
              </div>
              {tipoUsuario === 'cliente' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Empresa</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Building className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        {...register('empresa', { required: 'Empresa é obrigatória' })}
                        className="form-input pl-10 w-full"
                        placeholder="Nome da sua empresa"
                      />
                    </div>
                    {errors.empresa && <p className="text-red text-sm mt-1">{errors.empresa.message}</p>}
                  </div>
                  <div>
                    <label className="form-label">Telefone</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        {...register('telefone', { required: 'Telefone é obrigatório' })}
                        className="form-input pl-10 w-full"
                        placeholder="(11) 99999-9999"
                      />
                    </div>
                    {errors.telefone && <p className="text-red text-sm mt-1">{errors.telefone.message}</p>}
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Telefone</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Phone className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          {...register('telefone', { required: 'Telefone é obrigatório' })}
                          className="form-input pl-10 w-full"
                          placeholder="(11) 99999-9999"
                        />
                      </div>
                      {errors.telefone && <p className="text-red text-sm mt-1">{errors.telefone.message}</p>}
                    </div>
                    <div>
                      <label className="form-label">Nome da Empresa</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Building className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          {...register('nome_empresa', { required: 'Nome da empresa é obrigatório' })}
                          className="form-input pl-10 w-full"
                          placeholder="Nome da sua empresa"
                        />
                      </div>
                      {errors.nome_empresa && <p className="text-red text-sm mt-1">{errors.nome_empresa.message}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="form-label">CNPJ</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FileText className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        {...register('cnpj', { required: 'CNPJ é obrigatório' })}
                        className="form-input pl-10 w-full"
                        placeholder="00.000.000/0000-00"
                      />
                    </div>
                    {errors.cnpj && <p className="text-red text-sm mt-1">{errors.cnpj.message}</p>}
                  </div>
                  <div>
                    <label className="form-label">Endereço</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPin className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        {...register('endereco', { required: 'Endereço é obrigatório' })}
                        className="form-input pl-10 w-full"
                        placeholder="Endereço completo"
                      />
                    </div>
                    {errors.endereco && <p className="text-red text-sm mt-1">{errors.endereco.message}</p>}
                  </div>
                </>
              )}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="form-label mb-0">Senha</label>
                  <button
                    type="button"
                    onClick={generateSecurePassword}
                    className="btn-password-generator"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Gerar senha segura
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('senha', {
                      required: 'Senha é obrigatória',
                      minLength: { value: 6, message: 'Senha deve ter pelo menos 6 caracteres' }
                    })}
                    className="form-input pl-10 pr-16 w-full"
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-0 bottom-0 px-4 flex items-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-r-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red focus:ring-opacity-20"
                    title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {senha && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Força da senha:</span>
                      <span className={`text-xs font-medium ${getPasswordStrength(senha).color}`}>{getPasswordStrength(senha).label}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          getPasswordStrength(senha).score <= 2 ? 'bg-red-500' :
                          getPasswordStrength(senha).score === 3 ? 'bg-yellow-500' :
                          getPasswordStrength(senha).score === 4 ? 'bg-blue-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${(getPasswordStrength(senha).score / 5) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                {errors.senha && <p className="text-red text-sm mt-1">{errors.senha.message}</p>}
              </div>
              <div>
                <label className="form-label">Confirmar Senha</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...register('confirmarSenha', {
                      required: 'Confirmação de senha é obrigatória',
                      validate: (value) => value === senha || 'As senhas não coincidem'
                    })}
                    className="form-input pl-10 pr-16 w-full"
                    placeholder="Confirme sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-0 top-0 bottom-0 px-4 flex items-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-r-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red focus:ring-opacity-20"
                    title={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirmarSenha && <p className="text-red text-sm mt-1">{errors.confirmarSenha.message}</p>}
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary w-full"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="spinner mr-3"></div>
                    Criando conta...
                  </div>
                ) : (
                  `Criar Conta como ${tipoUsuario === 'cliente' ? 'Cliente' : 'Fornecedor'}`
                )}
              </button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Já tem uma conta?{' '}
                <Link to="/login" className="text-red hover:text-red/80 transition-colors font-medium">
                  Faça login
                </Link>
              </p>
            </div>
          </div>
          <div className="space-y-6">
            <div className="card bg-gradient-to-br from-red/5 to-red/10 border-red/20">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Por que se cadastrar na PeçaJá?</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">Acompanhe seus orçamentos, receba propostas, acompanhe as melhores cotações e tenha todo acompanhamento que só a PeçaJá com toda a experiência e conhecimento da Sobratema por trás pode trazer para você.</p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-white text-xs font-bold">1</span></div>
                  <div><h4 className="font-medium text-gray-900 mb-1">Cadastro Rápido</h4><p className="text-sm text-gray-600">Crie sua conta em menos de 2 minutos e comece a receber orçamentos</p></div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-white text-xs font-bold">2</span></div>
                  <div><h4 className="font-medium text-gray-900 mb-1">Orçamentos em Minutos</h4><p className="text-sm text-gray-600">Receba propostas de fornecedores qualificados em até 24 horas</p></div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-white text-xs font-bold">3</span></div>
                  <div><h4 className="font-medium text-gray-900 mb-1">Comparação Fácil</h4><p className="text-sm text-gray-600">Compare preços, prazos e qualidade de forma transparente</p></div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-white text-xs font-bold">4</span></div>
                  <div><h4 className="font-medium text-gray-900 mb-1">Suporte Especializado</h4><p className="text-sm text-gray-600">Conte com o apoio da Sobratema, referência no setor há mais de 30 anos</p></div>
                </div>
              </div>
            </div>
            <div className="card bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-3">Como funciona?</h4>
              <div className="space-y-3 text-sm text-gray-600">
                <p>1. Cadastre-se gratuitamente</p>
                <p>2. Faça seu pedido de peça</p>
                <p>3. Receba orçamentos de fornecedores</p>
                <p>4. Compare e escolha o melhor</p>
                <p>5. Feche o negócio com segurança</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Cadastro; 